// decompress.ts ↔ mpshared/decompress.cpp — the pdb record LZ decompressor.
//
// Format (DecompressChunk, decompress.cpp:8-98): flag bytes gate 8 items each, MSB
// first; flag=1 → literal byte; flag=0 → 16-bit BE code: high 3 bits = count-1
// (2..8), low 13 = back-offset; count bits 000 = extended code (offset 0 = END,
// else 14-bit offset from (code<<1 | next>>7) and count = (next & 0x7f) + 2).
// Self-overlapping back-copies replicate runs, so copies MUST go byte-forward
// (the C++ memcpy is forward on the frozen toolchain; this is the intended LZ
// semantics, not an accident we're preserving blindly).

export interface CompressionHeader {
  // decompress.h:8 (big-endian on disk, 6 bytes)
  fCompressed: number;
  cbUncompressed: number;
  cbCompressed: number;
}

export const kcbCompressionHeader = 6;

export function readCompressionHeader(b: Uint8Array, off: number): CompressionHeader {
  return {
    fCompressed: (b[off] << 8) | b[off + 1],
    cbUncompressed: (b[off + 2] << 8) | b[off + 3],
    cbCompressed: (b[off + 4] << 8) | b[off + 5],
  };
}

/** decompress.cpp:8-98 — decompress one chunk; returns bytes written and the new
 *  source offset. `cacheEnd`/`cbMax` model the chunked-into-cache arm (pbCacheEnd):
 *  back-references may reach into previously decompressed cache bytes. The
 *  single-shot path (DecompressToMemory) passes cacheEnd = null. */
export function DecompressChunk(
  src: Uint8Array,
  iSrc: number,
  dst: Uint8Array,
  cacheEnd: { bytes: Uint8Array; end: number } | null,
  cbMax: number,
): { cb: number; iSrc: number } {
  let pbSrc = iSrc;
  let pbDst = 0;
  const fChunk = cacheEnd !== null;

  let fDone = false;
  while (!fDone) {
    if (fChunk) {
      // If we're almost to the max size, break out (max copy size is 129)
      if (pbDst > cbMax - 8 * 129) break;
    }

    const bT = src[pbSrc++];
    for (let ibit = 7; ibit >= 0; ibit--) {
      if ((bT & (1 << ibit)) !== 0) {
        dst[pbDst++] = src[pbSrc++];
        continue;
      }

      const code = (src[pbSrc++] << 8) | src[pbSrc++];
      let offBackwards = code & 0x1fff;
      let cb: number;
      if ((code & 0xe000) !== 0) {
        cb = ((code & 0xe000) >> 13) + 1;
      } else {
        if (offBackwards === 0) {
          fDone = true;
          break;
        }
        // Extended match
        offBackwards = ((code << 1) | (src[pbSrc] >> 7)) & 0xffff;
        cb = (src[pbSrc++] & 0x7f) + 2;
      }

      const offStart = pbDst - offBackwards;
      if (offStart >= 0) {
        // Block is in local memory — byte-forward (self-overlap replicates runs)
        for (let i = 0; i < cb; i++) dst[pbDst + i] = dst[offStart + i];
        pbDst += cb;
      } else if (cb + offStart <= 0) {
        // Block is totally in cache memory
        const c = cacheEnd!;
        for (let i = 0; i < cb; i++) dst[pbDst + i] = c.bytes[c.end + offStart + i];
        pbDst += cb;
      } else {
        // Block is partly in both
        const c = cacheEnd!;
        for (let i = 0; i < -offStart; i++) dst[pbDst + i] = c.bytes[c.end + offStart + i];
        pbDst += -offStart;
        const rest = cb + offStart;
        for (let i = 0; i < rest; i++) dst[pbDst + i] = dst[i];
        pbDst += rest;
      }
    }
  }

  return { cb: pbDst, iSrc: pbSrc };
}

// decompress.cpp:100-105
export function DecompressToMemory(
  src: Uint8Array,
  iSrc: number,
  coh: CompressionHeader,
): Uint8Array {
  const out = new Uint8Array(coh.cbUncompressed);
  DecompressChunk(src, iSrc, out, null, 0);
  return out;
}
